import { useState, useMemo, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Restaurant } from "@/lib/supabase";
import { ChevronDown, ChevronUp, Navigation, ExternalLink, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
interface RestaurantMapProps {
  restaurants: Restaurant[];
  selectedCity: string;
  onJumpToRestaurant: (restaurantId: string) => void;
}
const MapComponent = lazyWithRetry(() => import("./RestaurantMapCanvas").then(m => ({ default: m.MapComponent })));
// Outer component that handles lazy loading
export const RestaurantMap = ({
  restaurants,
  selectedCity,
  onJumpToRestaurant
}: RestaurantMapProps) => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [keyLoading, setKeyLoading] = useState(false);

  // Filter restaurants with valid coordinates
  const restaurantsWithCoords = useMemo(() => {
    return restaurants.filter(r => r.gps_lat && r.gps_lng && r.gps_lat !== 0 && r.gps_lng !== 0);
  }, [restaurants]);
  const handleOpenMap = async () => {
    if (isMapOpen) {
      setIsMapOpen(false);
      return;
    }

    // Only fetch API key when user clicks to open
    if (!apiKey) {
      setKeyLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke("get-google-maps-key");
        if (error) {
          console.error("Error fetching API key:", error);
          setKeyLoading(false);
          return;
        }
        setApiKey(data?.apiKey || null);
      } catch (error) {
        console.error("Error fetching API key:", error);
        setKeyLoading(false);
        return;
      }
      setKeyLoading(false);
    }
    setIsMapOpen(true);
  };
  if (!selectedCity) {
    return null;
  }
  if (restaurantsWithCoords.length === 0) {
    return null;
  }

  // Show placeholder when map is not open
  if (!isMapOpen) {
    return <div className="mb-6">
        <div className="w-full bg-primary text-primary-foreground rounded-t-lg px-4 py-2 text-center font-semibold">
          Térkép
        </div>
        <button onClick={handleOpenMap} disabled={keyLoading} className="w-full bg-muted hover:bg-muted/80 rounded-b-lg px-4 py-4 text-center transition-colors cursor-pointer border-2 border-t-0 border-dashed border-border hover:border-primary/50 group" style={{
        height: "100px"
      }}>
          <div className="flex flex-col items-center justify-center gap-2">
            <MapPin className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-muted-foreground group-hover:text-foreground font-medium transition-colors">
              {keyLoading ? "Térkép betöltése..." : "Térkép nézethez kattints ide"}
            </span>
          </div>
        </button>
      </div>;
  }

  // Show loading state while fetching API key
  if (!apiKey) {
    return <div className="mb-6">
        <div className="bg-primary text-primary-foreground rounded-t-lg px-4 py-2 text-center font-semibold">
          Térkép
        </div>
        <div className="h-[500px] bg-muted animate-pulse rounded-b-lg flex items-center justify-center">
          <span className="text-muted-foreground">Térkép betöltése...</span>
        </div>
      </div>;
  }
  return <div>
      <Suspense fallback={<div className="h-[500px] bg-muted flex items-center justify-center text-muted-foreground">Térkép betöltése...</div>}>
        <MapComponent restaurants={restaurantsWithCoords} apiKey={apiKey} onJumpToRestaurant={onJumpToRestaurant} />
      </Suspense>
      <button onClick={handleOpenMap} className="w-full bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-center text-sm text-muted-foreground hover:text-foreground transition-colors -mt-4 mb-6">
        <ChevronUp className="h-4 w-4 inline mr-1" />
        Térkép bezárása
      </button>
    </div>;
};